import React, { forwardRef, type ComponentPropsWithRef } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';

import decorate from '../decorate/decorate';
import Link from '../link/link';
import type { Lucid } from '../../types/types';
import type { LinkOptions } from '../link/link';

export type ButtonTypes = 'button' | 'submit' | 'reset';
export type ButtonTagsType = 'button' | 'a' | typeof Link;

export type HTMLButtonProps = Omit<ComponentPropsWithRef<'button'>, 'size' | 'type'>;

export interface ButtonOptions {
  /** Render the button component as an `a` HTML tag with button styles. */
  displayAsButton?: boolean;
  /** Checks if the button should be disabled. */
  isDisabled?: boolean;
  /** Checks if the button should have inverse styles. */
  isInverse?: boolean;
  /** The available sizes for the Button component. */
  size?: Lucid.Theme.Component.ButtonSize['Variant'];
  /** The available types for the Button component. */
  type?: ButtonTypes;
  excludeFromGatsby?: boolean | null;
}

export interface ButtonProps extends LinkOptions, ButtonOptions, HTMLButtonProps {
  /** Controls the HTML tag created for the Button component. */
  as?: ButtonTagsType;
  /** The children passed to the Button component. */
  children?: React.ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The available variants for the Button component. */
  variant?: Lucid.Theme.Component.Button['Variant'];
}

const StyledButton = styled.button<ButtonProps>(
  variant({
    prop: 'size',
    scale: 'buttonSize',
    variants: {
      defaultVariant: {},
    },
  }),
  variant({
    scale: 'buttons',
    variants: {
      defaultVariant: {},
    },
  }),
  {
    label: 'button',
    // display: 'inline-block',
    // cursor: 'pointer',
    // textDecoration: 'none',
    // fontSize: 'inherit',
    // lineHeight: 1,
    // transition: 'transform 0.15s ease-out',
    // fontWeight: theme.fontWeights.strong,
    // minWidth: 'max-content',
    // textAlign: 'center',
  },
);

const ButtonComponent = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      as = 'button',
      children,
      className,
      displayAsButton = false,
      href,
      isDisabled = false,
      isInverse = false,
      label,
      size = 'medium',
      to,
      type = 'button',
      variant = 'primary',
      excludeFromGatsby = false,
      ...rest
    },
    forwardedRef,
  ) => {
    const linkLabel = label ? label : children;
    const hrefTo = href ? href : to ? to : undefined;
    const buttonElement: ButtonTagsType = excludeFromGatsby ? 'a' : displayAsButton ? Link : as;

    return (
      <StyledButton
        {...rest}
        ref={forwardedRef}
        aria-disabled={isDisabled || rest['aria-disabled']}
        as={buttonElement}
        className={className}
        href={hrefTo}
        isInverse={isInverse}
        size={size}
        type={type}
        variant={variant}
      >
        {linkLabel}
      </StyledButton>
    );
  },
);

// manually adding display name or it goes missing on build
ButtonComponent.displayName = 'Button';
const Button = decorate<ButtonProps>(ButtonComponent);

export { Button };
export default Button;
